import { z } from "zod";
import { outputSchema } from "./schema";
import { outputSettingsKey } from "./output-workflow";
import {
  resourceRefSchema,
  type Resource,
  type TenantSettings,
} from "./catalog";
export const outputMergeSchema = z
  .object({
    groups: z
      .array(
        z
          .object({
            keep: resourceRefSchema,
            duplicates: z.array(resourceRefSchema).min(1).max(500),
            merged: outputSchema.optional(),
          })
          .strict(),
      )
      .min(1)
      .max(100),
  })
  .strict();
export type OutputMerge = z.infer<typeof outputMergeSchema>;
export function outputMergeKey(resource: Resource) {
  const value = outputSchema.parse(resource.value);
  return JSON.stringify([
    value.role,
    outputSettingsKey(value),
    value.productionProfile?.id,
    value.productionProfile?.version,
    value.sheetProfile?.id,
    value.sheetProfile?.version,
  ]);
}
/** Profiles share a purpose and format; quality and rendering choices are compared. */
export function outputSimilarityKey(resource: Resource) {
  const v = outputSchema.parse(resource.value);
  return JSON.stringify([
    v.format,
    v.colorSpace,
    v.role,
    v.productionProfile?.id,
    v.productionProfile?.version,
    v.sheetProfile?.id,
    v.sheetProfile?.version,
  ]);
}
export function recommendedOutput(profiles: Resource[]) {
  const values = profiles.map((row) => outputSchema.parse(row.value));
  const best = [...values].sort(
    (a, b) =>
      Number(!!a.rasterize) - Number(!!b.rasterize) ||
      b.dpi - a.dpi ||
      b.quality - a.quality,
  )[0];
  return outputSchema.parse({
    ...best,
    dpi: Math.max(...values.map((v) => v.dpi)),
    quality: Math.max(...values.map((v) => v.quality)),
    minDpi: Math.max(...values.map((v) => v.minDpi)),
  });
}
export function commonOutputProfiles(
  resources: Resource[],
  settings: TenantSettings,
) {
  const latest = new Map<string, Resource>();
  for (const resource of resources)
    if (
      resource.kind === "output" &&
      !resource.projectId &&
      (!latest.has(resource.id) ||
        latest.get(resource.id)!.version < resource.version)
    )
      latest.set(resource.id, resource);
  const groups = new Map<string, Resource[]>();
  for (const resource of latest.values())
    if (!resource.archived) {
      const key = outputSimilarityKey(resource);
      groups.set(key, [...(groups.get(key) || []), resource]);
    }
  const defaults = new Set(
    Object.values(settings.defaultResources).map((ref) => ref?.id),
  );
  return [...groups.values()]
    .filter((rows) => rows.length > 1)
    .map((rows) => ({
      recommended: recommendedOutput(rows),
      profiles: rows.map((row) => ({
        id: row.id,
        version: row.version,
        name: row.name,
        isDefault: defaults.has(row.id),
        value: outputSchema.parse(row.value),
      })),
      recommendedId:
        rows.find((row) => defaults.has(row.id))?.id ||
        [...rows].sort((a, b) => {
          const av = outputSchema.parse(a.value),
            bv = outputSchema.parse(b.value);
          return (
            Number(!!av.rasterize) - Number(!!bv.rasterize) ||
            bv.dpi - av.dpi ||
            bv.quality - av.quality
          );
        })[0].id ||
        rows[0].id,
    }));
}
